import { useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi'
import { Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

import {
  Box,
  Button,
  Center,
  HStack,
  IconButton,
  Input,
  Spinner,
  Text,
} from '@chakra-ui/react'

// Ensure PDF.js worker is configured
import '../utils/pdfConfig'

const PAGE_HEIGHT = 320

interface PdfPageSelectorIProps {
  pdfFile: File
  selectedPage: number
  onPageChange: (pageNumber: number) => void
  onConfirm?: () => void
  isDisabled?: boolean
}

export const PdfPageSelector = ({
  pdfFile,
  selectedPage,
  onPageChange,
  onConfirm,
  isDisabled = false,
}: PdfPageSelectorIProps) => {
  const mode = useLightMode()
  const [numPages, setNumPages] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const handleLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setIsLoading(false)
    setError(null)
  }

  const handleLoadError = () => {
    setError('Failed to load PDF')
    setIsLoading(false)
  }

  const handlePrevPage = () => {
    if (selectedPage > 1) {
      onPageChange(selectedPage - 1)
    }
  }

  const handleNextPage = () => {
    if (selectedPage < numPages) {
      onPageChange(selectedPage + 1)
    }
  }

  return (
    <Box mt={4}>
      <Text
        color={`text.header.${mode}`}
        fontSize='16px'
        fontWeight='500'
        letterSpacing='-0.8px'
        lineHeight='24px'
        mb={2}
        opacity={0.5}
      >
        Confirm your page selection, then you will be able to crop the page.
      </Text>

      <Box
        bg='gray.50'
        border='1px solid'
        borderColor='gray.200'
        borderRadius='md'
        overflow='hidden'
        p={4}
      >
        {/* PDF Preview */}
        <Center minH='300px'>
          {isLoading && <Spinner color='blue.500' size='lg' />}
          {error && (
            <Text color='red.500' fontSize='sm'>
              {error}
            </Text>
          )}
          <Document
            file={pdfFile}
            loading={null}
            onLoadError={handleLoadError}
            onLoadSuccess={handleLoadSuccess}
          >
            <Page
              height={PAGE_HEIGHT}
              loading={null}
              pageNumber={selectedPage}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </Document>
        </Center>

        {/* Page Navigation */}
        {numPages > 0 && (
          <HStack justify='center' mt={4} spacing={4}>
            <IconButton
              aria-label='Previous page'
              icon={<HiChevronLeft size={20} />}
              isDisabled={isDisabled || selectedPage <= 1}
              onClick={handlePrevPage}
              size='sm'
              variant='outline'
            />
            <Text
              color={`text.primary.${mode}`}
              fontSize='14px'
              fontWeight='medium'
            >
              Page
            </Text>
            <Input
              backgroundColor='white'
              isDisabled={isDisabled}
              max={numPages}
              min={1}
              onChange={(e) => {
                const page = parseInt(e.target.value, 10)
                if (page >= 1 && page <= numPages) {
                  onPageChange(page)
                }
              }}
              rounded='6px'
              size='sm'
              textAlign='center'
              type='number'
              value={selectedPage}
              width='60px'
            />
            <Text
              color={`text.primary.${mode}`}
              fontSize='14px'
              fontWeight='medium'
            >
              of {numPages}
            </Text>
            <IconButton
              aria-label='Next page'
              icon={<HiChevronRight size={20} />}
              isDisabled={isDisabled || selectedPage >= numPages}
              onClick={handleNextPage}
              size='sm'
              variant='outline'
            />
          </HStack>
        )}

        {onConfirm && (
          <HStack justify='flex-end' mt={3}>
            <Button
              colorScheme='blue'
              isDisabled={isDisabled || isLoading || !!error || numPages === 0}
              onClick={onConfirm}
              size='sm'
            >
              Confirm Page
            </Button>
          </HStack>
        )}
      </Box>
    </Box>
  )
}
